// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { InfoWindow, Marker } from '@react-google-maps/api';
import MapBox from 'components/atoms/MapBox';
import { cloneElement, useEffect, useMemo, useRef, useState } from 'react';
import {
  getUserLocation,
  mergeClasses,
  removeMapStateFromURL,
  uniqRandomNumber,
} from 'util/commons';
import { Area } from 'util/Types';
import styles from './CollectAreasMap.module.scss';
import { CollectAreaStatusIcon } from 'components/organisms/CollectReceived';
import IconUserLocation from 'components/atoms/icons/IconUserLocation';
import IconMapMarkersGroup from 'components/atoms/icons/IconMapMarkersGroup';

type CollectAreasMapProps = {
  data: Area[];
  renderCollectButton: (area: Area) => JSX.Element;
};

const CollectAreasMap: React.FC<CollectAreasMapProps> = ({
  data,
  renderCollectButton,
}) => {
  const [mapKey, setMapKey] = useState<number>();
  const [map, setMap] = useState<google.maps.Map>();
  const [userCoordinates, setUserCoordinates] =
    useState<GeolocationCoordinates>();
  const [selectedMarkerId, setSelectedMarkerId] = useState<string>();
  const infoWindowRef = useRef<InfoWindow>(null);
  const watchIdRef = useRef<number>();
  const markersInitializedRef = useRef<boolean>(false);
  const userCoordinatesInitializedRef = useRef<boolean>(false);

  const getStateFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const lat = urlParams.get('lat');
    const lng = urlParams.get('lng');
    const zoom = urlParams.get('zoom');
    const keyword = urlParams.get('site-keyword');
    const siteGroupId = urlParams.get('siteGroupId');

    return {
      mapState:
        lat && lng && zoom
          ? {
              lat: parseFloat(lat),
              lng: parseFloat(lng),
              zoom: parseInt(zoom),
            }
          : null,
      keyword,
      siteGroupId,
    };
  };

  useEffect(() => {
    // Get initial location
    getUserLocation({
      successCallback: ({ coords }) => {
        setUserCoordinates(coords);
        userCoordinatesInitializedRef.current = true;
      },
      errorCallback: () => {
        userCoordinatesInitializedRef.current = true;
      },
    });

    // Start watching position for realtime updates
    if (navigator.geolocation) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          setUserCoordinates(position.coords);
        },
        (error) => {
          console.error('Error watching position:', error);
        }
      );
    }

    // Cleanup function to stop watching position
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  const bounds = useMemo(() => {
    const areaBounds = data.map(
      ({ areaLatitude, areaLongitude }) =>
        ({
          lat: Number(areaLatitude),
          lng: Number(areaLongitude),
        } as unknown as google.maps.LatLngAltitude)
    );

    if (userCoordinates) {
      areaBounds.push({
        lat: userCoordinates.latitude,
        lng: userCoordinates.longitude,
      } as unknown as google.maps.LatLngAltitude);
    }

    return areaBounds;
  }, [data, userCoordinates]);

  const markers = useMemo(() => {
    if (!window.google?.maps?.Size || !window.google?.maps?.Point) {
      return [];
    }

    const areaMarkers = data.map((area) => (
      <Marker
        key={area.areaId}
        position={{
          lat: Number(area.areaLatitude),
          lng: Number(area.areaLongitude),
        }}
        icon={{
          url: `data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2225%22%20height%3D%2241%22%20viewBox%3D%220%200%2025%2041%22%3E%3Cpath%20d%3D%22M12.5%200C5.6%200%200%205.6%200%2012.5c0%206.9%2012.5%2028.5%2012.5%2028.5S25%2019.4%2025%2012.5C25%205.6%2019.4%200%2012.5%200z%22%20fill%3D%22%23${
            area.emissionFlg ? '86ea5c' : '9e9e9e'
          }%22/%3E%3Ccircle%20cx%3D%2212.5%22%20cy%3D%2212.5%22%20r%3D%227%22%20fill%3D%22white%22/%3E%3C/svg%3E`,
          scaledSize: new window.google.maps.Size(25, 41),
          anchor: new window.google.maps.Point(12.5, 41),
          labelOrigin: new window.google.maps.Point(12.5, -12),
        }}
        label={{
          text: area.areaName || '',
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: 'bold',
          className: styles.markerLabel,
        }}
        title={area.areaName}
        onMouseDown={() =>
          setSelectedMarkerId(
            selectedMarkerId === area.areaId ? undefined : area.areaId
          )
        }
      >
        {selectedMarkerId === area.areaId && (
          <InfoWindow
            position={{
              lat: Number(area.areaLatitude),
              lng: Number(area.areaLongitude),
            }}
            ref={infoWindowRef}
            options={{
              disableAutoPan: true,
              maxWidth: 300,
              minWidth: 200,
            }}
            onDomReady={() => {
              infoWindowRef.current?.containerElement?.parentElement?.parentElement
                ?.getElementsByClassName('gm-ui-hover-effect')[0]
                ?.remove();
            }}
          >
            <div
              className={mergeClasses(
                'collect-area-card',
                styles.infoWindowContent
              )}
            >
              <CollectAreaStatusIcon
                className={styles.statusIcon}
                area={area}
              />
              <h4 className={styles.siteTitle}>{area.siteName}</h4>
              <div className={styles.buttonContainer}>
                {cloneElement(renderCollectButton(area), {
                  onClick: (e: unknown) => {
                    if (map) {
                      const url = new URL(window.location.href);
                      const center = map.getCenter();
                      const zoom = map.getZoom();

                      if (center && zoom) {
                        url.searchParams.set('lat', center.lat().toString());
                        url.searchParams.set('lng', center.lng().toString());
                        url.searchParams.set('zoom', zoom.toString());
                        window.history.replaceState({}, '', url.toString());
                      }
                    }
                    renderCollectButton(area).props.onClick?.(e);
                  },
                })}
              </div>
            </div>
          </InfoWindow>
        )}
      </Marker>
    ));

    // Add user location marker if available
    if (userCoordinates) {
      areaMarkers.push(
        <Marker
          key="user-location"
          position={{
            lat: userCoordinates.latitude,
            lng: userCoordinates.longitude,
          }}
          icon={{
            url: 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%3E%3Ccircle%20cx%3D%2210%22%20cy%3D%2210%22%20r%3D%228%22%20fill%3D%22%234285F4%22%20stroke%3D%22white%22%20stroke-width%3D%222%22/%3E%3C/svg%3E',
            scaledSize: new window.google.maps.Size(20, 20),
          }}
        />
      );
    }

    return areaMarkers;
  }, [
    data,
    userCoordinates,
    window.google?.maps?.Size,
    window.google?.maps?.Point,
    selectedMarkerId,
    renderCollectButton,
  ]);

  const fitMap = (map: google.maps.Map, type?: 'user' | 'markers') => {
    const { mapState, keyword, siteGroupId } = getStateFromURL();

    if (!type) {
      if (mapState) {
        map.setCenter({ lat: mapState.lat, lng: mapState.lng });
        map.setZoom(mapState.zoom);
        removeMapStateFromURL();

        return;
      }
    }

    if (
      userCoordinates &&
      ((type !== 'markers' && !keyword && !siteGroupId) || type === 'user')
    ) {
      // Focus on user location with 10km radius
      const userCenter = new window.google.maps.LatLng(
        userCoordinates.latitude,
        userCoordinates.longitude
      );
      map.setCenter(userCenter);

      // Calculate zoom level for 10km radius
      // Zoom level 13 approximately shows 10km radius
      map.setZoom(13);
    } else if (bounds.length > 0 && window.google?.maps?.LatLngBounds) {
      // Fallback to showing all points if no user location
      const latLngBounds = new window.google.maps.LatLngBounds();
      bounds.forEach((bound) => latLngBounds.extend(bound));
      map.fitBounds(latLngBounds);
    }
  };

  const handleActions = (type: 'user' | 'markers') => {
    if (map) {
      fitMap(map, type);
    }
  };

  useEffect(() => {
    if (
      map &&
      !markersInitializedRef.current &&
      userCoordinatesInitializedRef.current
    ) {
      if (userCoordinates || bounds.length > 0) {
        fitMap(map);
        markersInitializedRef.current = true;
      }
    }
  }, [map, bounds, window.google?.maps?.LatLngBounds, userCoordinates]);

  useEffect(() => {
    if (map) {
      const handleMapClick = () => {
        setSelectedMarkerId(undefined);
      };

      const listener = map.addListener('click', handleMapClick);

      return () => {
        if (listener) {
          window.google?.maps?.event?.removeListener(listener);
        }
      };
    }
  }, [map]);

  useEffect(() => {
    setMapKey(uniqRandomNumber());
    markersInitializedRef.current = false;
  }, [data]);

  return (
    <>
      <MapBox
        key={mapKey}
        bounds={bounds}
        onCreatedMap={setMap}
        drawInMap={markers}
        isDarkMode
      />

      <div className={styles.actionButton}>
        {userCoordinates && (
          <button onClick={() => handleActions('user')}>
            <IconUserLocation />
          </button>
        )}

        {bounds.length > 1 && (
          <button onClick={() => handleActions('markers')}>
            <IconMapMarkersGroup />
          </button>
        )}
      </div>
    </>
  );
};

export default CollectAreasMap;
